import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsRow from '@/components/feature/NewsRow';
import Pagination from '@/components/feature/Pagination';
import { useSEO } from '@/hooks/useSEO';
import { allNews, newsCategories } from '@/utils/data';

const PAGE_SIZE = 5;

export default function NewsListPage() {
  useSEO({
    title: '篮球新闻与赛事分析 - 篮球比分',
    description: '汇集赛事新闻、赛后分析与转会消息等篮球资讯，帮助球迷了解赛场内外的动态与深度解读。',
    keywords: '篮球新闻,篮球资讯,赛后分析,转会消息,篮球赛事',
  });

  const [category, setCategory] = useState('全部');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (category === '全部') return allNews;
    return allNews.filter((n) => n.category === category);
  }, [category]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const switchCategory = (c: string) => {
    setCategory(c);
    setPage(1);
  };

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '新闻' }]} />
      <header className="mt-4 mb-5">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">篮球新闻资讯</h1>
        <p className="mt-2 text-sm md:text-[15px] text-foreground-600 max-w-3xl">
          赛事新闻、赛后分析与转会消息，带您了解篮球赛场内外的动态。
        </p>
      </header>

      <div className="flex items-center gap-2 flex-wrap mb-5">
        {newsCategories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => switchCategory(c)}
            className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              category === c ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:text-primary-600'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pageItems.map((n) => (
              <article key={n.id} className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors">
                <NewsRow news={n} showCover />
              </article>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 flex flex-col items-center text-center px-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-article-line text-2xl"></i>
          </span>
          <h2 className="font-heading font-bold text-lg text-foreground-900">暂无相关新闻</h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">该分类下暂时没有内容，您可以切换其他分类，或返回首页查看最新赛事。</p>
          <Link to="/" className="mt-5 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      )}
    </div>
  );
}